import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import './PractitionerCalendarTab.css';

/**
 * Calendar integration tab.
 *
 * PRIMARY  — iCal URL: practitioners paste a private .ics URL.
 *            Zero friction. No OAuth. Works with Google, Apple, Outlook.
 * ADVANCED — Google OAuth full sync (requires Client ID configured).
 */
export default function PractitionerCalendarTab({ practitioner }) {
  const [ical, setIcal]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [removing, setRemoving]   = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);
  const [urlInput, setUrlInput]   = useState('');
  const [nameInput, setNameInput] = useState('');
  const [showHelp, setShowHelp]   = useState(null);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connecting, setConnecting]     = useState(false);
  const [oauthError, setOauthError]     = useState(null);
  const [oauthSuccess, setOauthSuccess] = useState(null);

  // Load iCal connection
  const load = useCallback(async () => {
    if (!practitioner?.id) return;
    setLoading(true);
    try {
      const data = await apiService.getICalConnection(practitioner.id);
      setIcal(data);
      setUrlInput(data.icalUrl || '');
      setNameInput(data.calendarName || '');
    } catch (e) {
      if (e?.status === 404 || String(e).includes('404')) {
        setIcal(null);
      } else {
        setError('Could not load calendar settings.');
      }
    } finally {
      setLoading(false);
    }
  }, [practitioner?.id]);

  useEffect(() => { load(); }, [load]);

  // OAuth popup listener
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type !== 'google-calendar-oauth') return;
      setConnecting(false);
      if (event.data.success) {
        setOauthSuccess('Google Calendar connected as ' + (event.data.email || 'your account'));
      } else {
        setOauthError(event.data.message || 'Connection failed.');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Save iCal URL
  const handleSave = async () => {
    if (!urlInput.trim()) { setError('Please enter a calendar URL.'); return; }
    const lower = urlInput.toLowerCase();
    if (!lower.includes('.ics') && !lower.includes('ical') && !lower.includes('webcal')) {
      setError("That URL doesn't look like an iCal URL. It should end in .ics or contain 'ical'.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const url = urlInput.trim().replace(/^webcal:/i, 'https:');
      const data = await apiService.saveICalConnection(practitioner.id, {
        icalUrl: url,
        calendarName: nameInput.trim() || 'My Calendar'
      });
      setIcal(data);
      setSuccess('Calendar connected! Busy times sync automatically every 15 minutes.');
    } catch (e) {
      setError('Could not save: ' + (e?.message || String(e)));
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      await apiService.syncICalNow(practitioner.id);
      setSuccess('Sync started — usually completes in a few seconds.');
      setTimeout(load, 3000);
    } catch (e) {
      setError('Sync failed: ' + (e?.message || String(e)));
    } finally {
      setSyncing(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove this calendar connection?')) return;
    setRemoving(true);
    setError(null);
    try {
      await apiService.removeICalConnection(practitioner.id);
      setIcal(null);
      setUrlInput('');
      setNameInput('');
      setSuccess('Calendar disconnected.');
    } catch (e) {
      setError('Could not remove: ' + (e?.message || String(e)));
    } finally {
      setRemoving(false);
    }
  };

  const handleGoogleConnect = async () => {
    setConnecting(true);
    setOauthError(null);
    setOauthSuccess(null);
    try {
      const result = await apiService.getGoogleCalendarAuthUrl(practitioner.id);
      if (!result?.authUrl) {
        setOauthError(result?.message || 'Google Calendar not configured. Add ClientId/ClientSecret to appsettings.json.');
        setConnecting(false);
        return;
      }
      const popup = window.open(result.authUrl, 'google-calendar-oauth', 'width=500,height=650,scrollbars=yes,resizable=yes');
      if (!popup) { setOauthError('Popup blocked. Allow popups for this site.'); setConnecting(false); return; }
      const iv = setInterval(() => { if (popup.closed) { clearInterval(iv); setConnecting(false); } }, 1000);
    } catch (e) {
      setOauthError(String(e?.message || e));
      setConnecting(false);
    }
  };

  if (loading) {
    return <div className="cal-tab-loading"><span className="cal-spinner" /> Loading…</div>;
  }

  const isConnected = !!(ical?.icalUrl);
  const hasError    = ical?.status === 'Error';

  return (
    <div className="cal-tab">

      {error   && <div className="cal-banner cal-banner--error">⚠ {error}</div>}
      {success && <div className="cal-banner cal-banner--success">✓ {success}</div>}

      <div className={`cal-card ${isConnected && !hasError ? 'cal-card--connected' : ''} ${hasError ? 'cal-card--error' : ''}`}>
        <div className="cal-card-header">
          <div className="cal-card-icon">🗓</div>
          <div className="cal-card-info">
            <div className="cal-card-title">
              {isConnected ? (ical.calendarName || 'My Calendar') : 'Connect a Calendar'}
            </div>
            <div className="cal-card-subtitle">
              {isConnected
                ? (hasError
                    ? 'Last sync failed — check the URL is still valid'
                    : (ical.lastSyncTime
                        ? 'Last synced ' + new Date(ical.lastSyncTime).toLocaleString()
                        : 'Not yet synced'))
                : 'Block busy times and prevent double-bookings'}
            </div>
          </div>
          {isConnected && (
            <div className={`cal-badge ${hasError ? 'cal-badge--error' : 'cal-badge--active'}`}>
              {hasError ? 'Error' : 'Connected'}
            </div>
          )}
        </div>

        <div className="cal-card-body">
          <label className="cal-label" htmlFor="cal-url-input">
            {isConnected ? 'Calendar URL' : 'Paste your private calendar URL'}
          </label>
          <div className="cal-url-row">
            <input
              id="cal-url-input"
              className={`cal-url-input ${hasError ? 'cal-url-input--error' : ''}`}
              type="url"
              placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
              value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <button
              className="cal-btn cal-btn--primary"
              onClick={handleSave}
              disabled={saving || !urlInput.trim()}
            >
              {saving ? <><span className="cal-spinner cal-spinner--sm" /> Saving…</> : (isConnected ? 'Update' : 'Connect')}
            </button>
          </div>

          {isConnected && (
            <div className="cal-actions-row">
              <button className="cal-btn cal-btn--secondary" onClick={handleSync} disabled={syncing}>
                {syncing ? <><span className="cal-spinner cal-spinner--sm" /> Syncing…</> : '🔄 Sync Now'}
              </button>
              <button className="cal-btn cal-btn--danger" onClick={handleRemove} disabled={removing}>
                {removing ? 'Removing…' : 'Disconnect'}
              </button>
            </div>
          )}
        </div>
      </div>

      {!isConnected && (
        <div className="cal-help">
          <p className="cal-help-intro">Where do I find my calendar URL?</p>
          <div className="cal-help-tabs">
            {['google', 'apple', 'outlook'].map(provider => (
              <button
                key={provider}
                className={`cal-help-tab ${showHelp === provider ? 'cal-help-tab--active' : ''}`}
                onClick={() => setShowHelp(showHelp === provider ? null : provider)}
              >
                {provider === 'google'  && '📧 Google'}
                {provider === 'apple'   && '🍎 Apple'}
                {provider === 'outlook' && '📦 Outlook'}
              </button>
            ))}
          </div>

          {showHelp === 'google' && (
            <ol className="cal-steps">
              <li>Open <strong>Google Calendar</strong> on your computer</li>
              <li>Next to your calendar, click the three-dot menu ⋯</li>
              <li>Choose <strong>Settings and sharing</strong></li>
              <li>Scroll to <strong>Integrate calendar</strong></li>
              <li>Copy the <strong>Secret address in iCal format</strong> link</li>
              <li>Paste it in the URL box above and click <strong>Connect</strong></li>
            </ol>
          )}
          {showHelp === 'apple' && (
            <ol className="cal-steps">
              <li>Open <strong>Calendar</strong> on your Mac</li>
              <li>Right-click a calendar ▶ <strong>Share Calendar…</strong></li>
              <li>Enable <strong>Public Calendar</strong> and copy the link</li>
              <li>Paste it in the URL box above</li>
            </ol>
          )}
          {showHelp === 'outlook' && (
            <ol className="cal-steps">
              <li>Open <strong>Outlook</strong> and go to Calendar view</li>
              <li>Right-click a calendar ▶ <strong>Sharing permissions</strong></li>
              <li>Click <strong>Publish this calendar</strong></li>
              <li>Copy the <strong>ICS</strong> link and paste it above</li>
            </ol>
          )}

          <div className="cal-explainer-footer">
            <span>🔒</span>
            <span>Your URL is stored securely and only used to read busy times — your personal calendar data stays private.</span>
          </div>
        </div>
      )}

      <div className="cal-advanced">
        <button className="cal-advanced-toggle" onClick={() => setShowAdvanced(v => !v)}>
          {showAdvanced ? '▼' : '▶'} Advanced: Google OAuth full sync (read + write)
        </button>
        {showAdvanced && (
          <div className="cal-advanced-body">
            <p className="cal-advanced-desc">
              Connect via Google OAuth to both <strong>read</strong> busy times and <strong>write</strong> confirmed
              appointments to Google Calendar. Requires a Google Cloud OAuth app.
            </p>
            {oauthError   && <div className="cal-banner cal-banner--error" style={{marginBottom:8}}>⚠ {oauthError}</div>}
            {oauthSuccess && <div className="cal-banner cal-banner--success" style={{marginBottom:8}}>✓ {oauthSuccess}</div>}
            <button className="cal-btn cal-btn--google" onClick={handleGoogleConnect} disabled={connecting}>
              {connecting
                ? <><span className="cal-spinner cal-spinner--sm" /> Waiting for Google…</>
                : <>
                    <svg viewBox="0 0 24 24" className="cal-google-icon" aria-hidden="true">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Sign in with Google
                  </>
              }
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
